import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import type { PublicVideo } from "@/pages/Explore/types";

export async function fetchUserVideos(userId: string): Promise<Tables<"generated_videos">[]> {
  const { data, error } = await supabase
    .from("generated_videos")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data ?? [];
}

export interface PaginatedVideoResult {
  data: PublicVideo[];
  hasMore: boolean;
}

const EXPLORE_VIDEO_COLUMNS = "id, video_url, thumbnail_url, prompt, model, duration, resolution, aspect_ratio, pipeline, created_at, reference_image_url" as const;

export async function fetchPublicVideosPaginated(
  pageSize: number,
  cursor?: string,
  excludeUserId?: string,
): Promise<PaginatedVideoResult> {
  let query = supabase
    .from("generated_videos")
    .select(EXPLORE_VIDEO_COLUMNS)
    .eq("status", "completed")
    .order("created_at", { ascending: false })
    .limit(pageSize + 1);

  if (cursor) {
    query = query.lt("created_at", cursor);
  }

  if (excludeUserId) {
    query = query.neq("user_id", excludeUserId);
  }

  const { data, error } = await query;
  if (error) throw error;

  const rows = data ?? [];
  const hasMore = rows.length > pageSize;

  return {
    data: (hasMore ? rows.slice(0, pageSize) : rows) as PublicVideo[],
    hasMore,
  };
}

export async function fetchRandomPublicVideos(count: number): Promise<PublicVideo[]> {
  const { count: total, error: countErr } = await supabase
    .from("generated_videos")
    .select("id", { count: "exact", head: true })
    .eq("status", "completed");

  if (countErr) throw countErr;

  const safeTotal = total ?? 0;
  if (safeTotal === 0) return [];

  const maxOffset = Math.max(0, safeTotal - count);
  const offset = maxOffset > 0 ? Math.floor(Math.random() * maxOffset) : 0;

  const { data, error } = await supabase
    .from("generated_videos")
    .select(EXPLORE_VIDEO_COLUMNS)
    .eq("status", "completed")
    .order("created_at", { ascending: false })
    .range(offset, offset + count - 1);

  if (error) throw error;
  return (data ?? []) as PublicVideo[];
}

export interface PendingVideoRow {
  id: string;
  task_id: string | null;
  status: string;
  prompt: string;
  created_at: string;
}

export async function fetchPendingVideos(userId: string): Promise<PendingVideoRow[]> {
  const { data, error } = await supabase
    .from("generated_videos")
    .select("id, task_id, status, prompt, created_at")
    .eq("user_id", userId)
    .in("status", ["pending", "processing"])
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data ?? [];
}
